import React, { useCallback, useEffect, useState } from 'react';
import { batch } from 'react-redux';
import { ListChildComponentProps } from 'react-window';
import { List as ImmutableList } from 'immutable';
import { v4 as uuidv4 } from 'uuid';

import { useAppDispatch } from 'store/hooks';

import * as actions from 'modules/kid-zone/state/actions';
import * as selectors from 'modules/kid-zone/state/selectors';

import { PeakModules } from 'common/constants/peakModules';

import { IPersonSearchBarValues } from 'common/components/ManualCheckInPanel/components/PersonSearchBar/interfaces';
import { ICheckInManuallySearch, IKidProfileImt } from 'modules/kid-zone/interfaces';
import { ICheckInImt } from 'modules/front-desk/interfaces';

import { ManualCheckInCardItem } from 'modules/kid-zone/containers/index';
import { CheckInAllModal } from 'modules/kid-zone/components';
import AddDateOfBirthdayModal from './components/AddDateOfBirthdayModal/AddDateOfBirthdayModal';
import { ActionResult } from 'common/constants';
import { selectPersonProfile } from 'common/components/PersonProfile/state/selectors';
import { fetchKidProfileInfo } from 'modules/kid-zone/state/actions';
import useRootSelector from 'common/hooks/useRootSelector';
import { useLazyGetKidZoneMembers } from 'modules/kid-zone/api/kidZoneMember';
import { IPaginatedData } from 'common/interfaces/pagination';
import ManualCheckInPanel from './ManualCheckInPanel';

interface IProps {
  onClose: () => void;
  profileId?: number;
}

const ManualCheckIn = ({ onClose, profileId }: IProps): JSX.Element => {
  const dispatch = useAppDispatch();

  const checkInAllActionResult: ImmutableList<ICheckInImt> = useRootSelector(
    selectors.selectCheckInAllChildrenActionResult,
  );
  const addDOBActionResult = useRootSelector(selectors.selectAddDOBActionResult);
  const isAddDOBActionLoading: boolean = useRootSelector(selectors.selectAddDOBActionLoading);
  const kidProfile: IKidProfileImt | undefined = useRootSelector(
    selectPersonProfile(Number(profileId)),
  );
  const [isMembersLoading, setIsMembersLoading] = useState(false);
  const [searchMembers] = useLazyGetKidZoneMembers();
  const [members, setMembers] = useState<IPaginatedData<ICheckInManuallySearch>>({
    data: [],
    meta: { page: 0, perPage: 10, total: 0 },
  });
  const [searchValues, setSearchValues] = useState<IPersonSearchBarValues | undefined>(undefined);

  const [isSearchTyped, setIsSearchTyped] = useState(false);
  const [checkInAllChildren, setCheckInAllChildren] = useState<{
    cardIndex: number | null;
  }>({ cardIndex: null });
  const [personWithoutDOBData, setPersonWithoutDOBData] = useState<{
    childId: number | null;
    cardIndex: number | null;
  } | null>({
    childId: null,
    cardIndex: null,
  });

  useEffect(() => {
    if (checkInAllActionResult?.size) {
      setCheckInAllChildren({ cardIndex: null });
      onClose();
      batch(() => {
        dispatch(actions.resetCheckInAllChildrenActionResult());
        dispatch(actions.setKidZoneRightPanelPersonAction(null));
      });
    }
  }, [checkInAllActionResult, dispatch, onClose]);

  useEffect(() => {
    if (addDOBActionResult === ActionResult.SUCCESS_ACTION) {
      setPersonWithoutDOBData(prevState => {
        if (prevState?.childId) {
          return { childId: null, cardIndex: null };
        }

        return prevState;
      });

      if (kidProfile) {
        const other = kidProfile.get('other');
        const isExistPerson = other.some(item => item.get('id') === personWithoutDOBData?.childId);

        if (isExistPerson) {
          dispatch(fetchKidProfileInfo(Number(kidProfile.get('id'))));
        }
      }
      dispatch(actions.resetAddDateOfBirthActionResult());
    }
  }, [addDOBActionResult, dispatch, kidProfile, personWithoutDOBData]);

  const handleSearchParamsChange = useCallback(
    (search: IPersonSearchBarValues) => {
      setSearchValues(search);
      setIsMembersLoading(true);
      if (searchValues) {
        setMembers({
          data: [],
          meta: { page: 0, perPage: 10, total: 0 },
        });
      }
      searchMembers({ search, page: 0, perPage: 10 })
        .unwrap()
        .then(membersPayload => setMembers(membersPayload))
        .catch(() => {})
        .finally(() => setIsMembersLoading(false));
    },
    [searchMembers, searchValues],
  );

  const fetchMoreMembers = () => {
    setIsMembersLoading(true);
    searchMembers({ search: searchValues, page: members.meta.page + 1, perPage: 10 })
      .unwrap()
      .then(membersPayload =>
        setMembers(prev => ({
          meta: membersPayload.meta,
          data: [...prev.data, ...membersPayload.data],
        })),
      )
      .catch(() => {})
      .finally(() => setIsMembersLoading(false));
  };

  const handlePersonSearchBarFilled = useCallback((isFilled: boolean) => {
    setIsSearchTyped(isFilled);
  }, []);

  const handleCheckInKid = useCallback(
    (childId: number) => {
      onClose();
      dispatch(actions.setChildCheckInPanelId(childId));
    },
    [dispatch, onClose],
  );

  const handleCheckInAll = useCallback((cardIndex: number) => {
    setCheckInAllChildren({ cardIndex });
  }, []);

  const handleOpenAddDOBModal = useCallback((childId: number, cardIndex: number) => {
    setPersonWithoutDOBData({ childId, cardIndex });
  }, []);

  const addDateOfBirth = useCallback(
    (date: string) => {
      dispatch(
        actions.addDateOfBirth(
          Number(personWithoutDOBData?.childId),
          personWithoutDOBData?.cardIndex || null,
          date,
        ),
      );
    },
    [dispatch, personWithoutDOBData],
  );

  const renderMemberItem = useCallback(
    ({ index }: ListChildComponentProps) => {
      const member = members.data[index];

      if (!member) {
        return null;
      }

      return (
        <ManualCheckInCardItem
          member={member}
          index={index}
          key={uuidv4()}
          onCheckInKid={handleCheckInKid}
          onCheckInAll={handleCheckInAll}
          onAddBirthday={handleOpenAddDOBModal}
        />
      );
    },
    [handleCheckInAll, handleCheckInKid, handleOpenAddDOBModal, members.data],
  );

  let member: ICheckInManuallySearch | null | undefined = null;

  if (checkInAllChildren.cardIndex !== null) {
    member = members.data[checkInAllChildren.cardIndex];
  }

  return (
    <>
      <ManualCheckInPanel
        members={members.data}
        needToRefetch={addDOBActionResult === ActionResult.SUCCESS_ACTION}
        isMembersLoading={isMembersLoading}
        isSearchTyped={isSearchTyped}
        onClose={onClose}
        onSearchFieldChange={handleSearchParamsChange}
        setIsSearchTyped={handlePersonSearchBarFilled}
        renderMemberItem={renderMemberItem}
        module={PeakModules.KidZone}
        fetchMoreMembers={fetchMoreMembers}
      />

      {member && (
        <CheckInAllModal
          isOpen
          onAddBirthday={(id: number) =>
            handleOpenAddDOBModal(id, Number(checkInAllChildren.cardIndex))
          }
          member={member}
          onClose={() => setCheckInAllChildren({ cardIndex: null })}
        />
      )}

      {!!personWithoutDOBData?.childId && (
        <AddDateOfBirthdayModal
          isOpen={!!personWithoutDOBData.childId}
          onClose={() => setPersonWithoutDOBData(null)}
          onSubmit={addDateOfBirth}
          isAddDOBActionLoading={isAddDOBActionLoading}
        />
      )}
    </>
  );
};

export default ManualCheckIn;
